import { CSSProperties, useEffect } from "react";
import DisplayBlobImage from "../../../common/DisplayBlobImage";
import DisplayThumbnailImage from "../../../common/DisplayThumbnailImage";
import { isBlobImage } from "../../../utils/MessageUtils";
import { dimensionFromFileName } from "@home-health-notify/domain";

type MessageImageThumbnailProps = {
  url: string;
  text: string;
  isPendingMessage?: boolean;
  onImageWidthCalculated?: (width: number) => void;
  isVideoThumbnail?: boolean;
};

const MessageImageThumbnail = ({
  url,
  text,
  isPendingMessage = false,
  onImageWidthCalculated,
  isVideoThumbnail,
}: MessageImageThumbnailProps) => {
  const imageAndContainerStyle = (
    url: string,
  ): {
    imageContainerStyle: CSSProperties;
    imageStyle: CSSProperties;
    width: number;
  } => {
    const centerContentAndHideOverflowStyle = {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      overflow: "hidden",
    };

    const fileDimension = dimensionFromFileName(url);

    if (fileDimension) {
      const { width, height } = fileDimension;
      const isLandscape = width > height;
      const scaledWidth = isLandscape ? 330 : 240;
      const scaledHeight = (height * scaledWidth) / width;
      return {
        imageContainerStyle: {
          maxWidth: `${scaledWidth}px`,
          width: "100%",
          aspectRatio: `${scaledWidth} / ${Math.min(scaledHeight, scaledWidth * 1.4)}`,
          ...centerContentAndHideOverflowStyle,
        },
        imageStyle: {
          width: "100%",
          flexBasis: "auto",
          flexShrink: 0,
          flexGrow: 0,
        },
        width: scaledWidth,
      };
    }

    return {
      imageContainerStyle: {
        maxWidth: "330px",
        width: "100%",
        aspectRatio: "330 / 240",
      },
      imageStyle: {
        width: "100%",
        height: "100%",
        objectFit: "cover",
      },
      width: 330,
    };
  };

  const { imageContainerStyle, imageStyle, width } =
    imageAndContainerStyle(url);

  useEffect(() => {
    onImageWidthCalculated?.(width);
  }, [width, onImageWidthCalculated]);

  return (
    <>
      {/* TODO: Check if DisplayBlobImage is used in any flow */}
      {isBlobImage(url) ? (
        <DisplayBlobImage url={url} alt={text} />
      ) : (
        <DisplayThumbnailImage
          url={isPendingMessage ? "" : url}
          alt={text}
          style={imageStyle}
          imageContainerStyle={imageContainerStyle}
          isVideoThumbnail={isVideoThumbnail}
        />
      )}
    </>
  );
};

export default MessageImageThumbnail;
